import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useOnSelectionChange,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { Eye, EyeOff, Focus } from "lucide-react";
import { ArchiNode } from "./ArchiNode";
import { ArchiGroupNode } from "./ArchiGroupNode";
import { ArchiEdge } from "./ArchiEdge";
import { EdgeMarkers } from "./EdgeMarkers";
import { LAYERS, LAYER_COLORS, STATUS_OPTIONS, STATUS_COLORS, RELEASE_CANDIDATE_OPTIONS, RELEASE_CANDIDATE_COLORS } from "@/constants/archimate";

const NODE_TYPES = { archimate: ArchiNode, archimateGroup: ArchiGroupNode };
const EDGE_TYPES = { archimate: ArchiEdge };
const TOGGLEABLE_LAYERS = LAYERS.filter((l) => l.key !== "other");
const RC_FILTER_VALUES = ["Unset", ...RELEASE_CANDIDATE_OPTIONS];
// Matches backend GROUP_PAD/GROUP_HEADER so a group's manual-resize floor lines up with Tidy Up's fit
const GROUP_PAD = 30;
const GROUP_MIN_WIDTH = 220;
const GROUP_MIN_HEIGHT = 140;

function LayerTogglePanel({ hiddenLayers, onToggle }) {
  return (
    <div
      data-testid="layer-toggle-panel"
      className="bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-md p-2 space-y-0.5"
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-1.5 pb-1">Layers</div>
      {TOGGLEABLE_LAYERS.map((layer) => {
        const hidden = hiddenLayers.has(layer.key);
        const colors = LAYER_COLORS[layer.key];
        return (
          <button
            key={layer.key}
            data-testid={`layer-visibility-toggle-${layer.key}`}
            onClick={() => onToggle(layer.key)}
            className={`flex items-center gap-2 w-full px-1.5 py-1 rounded-md text-xs transition-colors ${
              hidden ? "text-slate-400" : "text-slate-700"
            } hover:bg-slate-100`}
          >
            <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: hidden ? "#CBD5E1" : colors.border }} />
            <span className="flex-1 text-left truncate">{layer.label}</span>
            {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        );
      })}
    </div>
  );
}

function StatusLegendPanel({ hiddenStatuses, onToggle }) {
  return (
    <div
      data-testid="status-legend-panel"
      className="bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-md p-2 space-y-0.5"
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-1.5 pb-1">Status</div>
      {STATUS_OPTIONS.map((status) => {
        const c = STATUS_COLORS[status];
        const hidden = hiddenStatuses.has(status);
        return (
          <button
            key={status}
            data-testid={`status-visibility-toggle-${status}`}
            onClick={() => onToggle(status)}
            className={`flex items-center gap-2 w-full px-1.5 py-1 rounded-md text-xs transition-colors ${
              hidden ? "text-slate-400" : "text-slate-700"
            } hover:bg-slate-100`}
          >
            <span
              className="h-2.5 w-2.5 rounded-full border border-white shadow-sm flex-shrink-0"
              style={{ background: hidden ? "#CBD5E1" : c.dot }}
            />
            <span className="flex-1 text-left truncate">{c.label}</span>
            {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        );
      })}
    </div>
  );
}

function ReleaseCandidateLegendPanel({ hiddenReleaseCandidates, onToggle }) {
  return (
    <div
      data-testid="release-candidate-legend-panel"
      className="bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-md p-2 space-y-0.5"
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-1.5 pb-1">Release Candidate</div>
      {RC_FILTER_VALUES.map((rc) => {
        const c = RELEASE_CANDIDATE_COLORS[rc];
        const hidden = hiddenReleaseCandidates.has(rc);
        return (
          <button
            key={rc}
            data-testid={`release-candidate-visibility-toggle-${rc}`}
            onClick={() => onToggle(rc)}
            className={`flex items-center gap-2 w-full px-1.5 py-1 rounded-md text-xs transition-colors ${
              hidden ? "text-slate-400" : "text-slate-700"
            } hover:bg-slate-100`}
          >
            <span
              className="h-2.5 w-2.5 rounded-full border border-white shadow-sm flex-shrink-0"
              style={{ background: hidden ? "#CBD5E1" : c.dot }}
            />
            <span className="flex-1 text-left truncate">{c.label}</span>
            {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        );
      })}
    </div>
  );
}


function getAbsolutePosition(node, allNodes) {
  if (!node.parentNode) return node.position;
  const parent = allNodes.find((n) => n.id === node.parentNode);
  if (!parent) return node.position;
  return { x: parent.position.x + node.position.x, y: parent.position.y + node.position.y };
}

function ArchiCanvas({
  view,
  elementsById,
  onNodeMove,
  onNodeClick,
  onRemoveNode,
  onConnectElements,
  onUpdateRelationship,
  onDeleteRelationship,
  onNodeResize,
  onNodeReparent,
  onToggleCollapse,
  onSelectionChange,
  onUngroup,
  focusRequest,
  readOnly = false,
  showKey = true,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [hiddenLayers, setHiddenLayers] = useState(new Set());
  const [hiddenStatuses, setHiddenStatuses] = useState(new Set());
  const [hiddenReleaseCandidates, setHiddenReleaseCandidates] = useState(new Set());
  const [openRelationshipId, setOpenRelationshipId] = useState(null);
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const lastFocusNonceRef = useRef(null);
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (!focusRequest || focusRequest.nonce === lastFocusNonceRef.current) return;
    const node = nodes.find((n) => n.data?.element?.id === focusRequest.elementId);
    if (node) {
      setFocusedNodeId(node.id);
      lastFocusNonceRef.current = focusRequest.nonce;
    }
  }, [focusRequest, nodes]);

  useEffect(() => {
    if (!view) {
      setNodes([]);
      setEdges([]);
      return;
    }
    const viewNodes = view.nodes || [];
    const validNodeIds = new Set(viewNodes.map((n) => n.node_id));
    const sorted = [...viewNodes].sort((a, b) => {
      const aGroup = elementsById[a.element_id]?.type === "grouping" ? 0 : 1;
      const bGroup = elementsById[b.element_id]?.type === "grouping" ? 0 : 1;
      return aGroup - bGroup;
    });
    setNodes(
      sorted.map((n) => {
        const element = elementsById[n.element_id] || { id: n.element_id, name: "Unknown", type: "business-object", layer: "business" };
        const isGroup = element.type === "grouping";
        const collapsed = !!n.collapsed;
        const hasValidParent = n.parent_node_id && validNodeIds.has(n.parent_node_id);
        return {
          id: n.node_id,
          type: isGroup ? "archimateGroup" : "archimate",
          position: { x: n.x, y: n.y },
          style: collapsed ? { width: 220, height: 48 } : { width: n.width, height: n.height },
          parentNode: hasValidParent ? n.parent_node_id : undefined,
          zIndex: isGroup ? 0 : 1,
          data: {
            element,
            onClick: onNodeClick,
            onRemove: onRemoveNode,
            collapsed,
            onToggleCollapse: () => onToggleCollapse(n.node_id, !collapsed),
            onUngroup: () => onUngroup(n.node_id),
            onResizeEnd: (w, h) => onNodeResize(n.node_id, w, h),
            readOnly,
          },
        };
      })
    );
    setEdges(
      (view.edges || []).map((e) => ({
        id: e.id,
        source: e.source_node_id,
        target: e.target_node_id,
        type: "archimate",
        data: {
          relType: e.type,
          name: e.name,
          lineStyle: view.line_style || "curved",
          sourceLabel: elementsById[viewNodes.find((n) => n.node_id === e.source_node_id)?.element_id]?.name || "Unknown",
          targetLabel: elementsById[viewNodes.find((n) => n.node_id === e.target_node_id)?.element_id]?.name || "Unknown",
          readOnly,
          onUpdate: (fields) => onUpdateRelationship?.(e.id, fields),
          onDelete: () => onDeleteRelationship?.(e.id),
        },
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, elementsById, readOnly]);

  useEffect(() => {
    if (!view) return;
    const raf = requestAnimationFrame(() => fitView({ padding: 0.15, duration: 0 }));
    return () => cancelAnimationFrame(raf);
  }, [view?.id, fitView]);

  useOnSelectionChange({
    onChange: ({ nodes: selected }) => {
      onSelectionChange?.(selected.filter((n) => n.type !== "archimateGroup").map((n) => n.id));
    },
  });

  const collapsedGroupIds = useMemo(() => new Set(nodes.filter((n) => n.data?.collapsed).map((n) => n.id)), [nodes]);

  const neighborInfo = useMemo(() => {
    if (!focusedNodeId) return null;
    const ids = new Set([focusedNodeId]);
    const edgeIds = new Set();
    edges.forEach((e) => {
      if (e.source === focusedNodeId || e.target === focusedNodeId) {
        ids.add(e.source);
        ids.add(e.target);
        edgeIds.add(e.id);
      }
    });
    return { ids, edgeIds };
  }, [focusedNodeId, edges]);

  const groupMinSizes = useMemo(() => {
    const minSizeByGroupId = {};
    nodes.forEach((n) => {
      if (n.type !== "archimateGroup") return;
      const children = nodes.filter((c) => c.parentNode === n.id);
      if (!children.length) {
        minSizeByGroupId[n.id] = { minWidth: GROUP_MIN_WIDTH, minHeight: GROUP_MIN_HEIGHT };
        return;
      }
      const maxRight = Math.max(...children.map((c) => c.position.x + (c.style?.width || 180)));
      const maxBottom = Math.max(...children.map((c) => c.position.y + (c.style?.height || 70)));
      minSizeByGroupId[n.id] = {
        minWidth: Math.max(GROUP_MIN_WIDTH, maxRight + GROUP_PAD),
        minHeight: Math.max(GROUP_MIN_HEIGHT, maxBottom + GROUP_PAD),
      };
    });
    return minSizeByGroupId;
  }, [nodes]);

  const groupEmptyIds = useMemo(() => {
    const groupNodes = nodes.filter((n) => n.type === "archimateGroup");
    const isElementHidden = (n) => {
      const el = n.data.element;
      return (
        hiddenLayers.has(el.layer) ||
        hiddenStatuses.has(el.status || "Dev") ||
        hiddenReleaseCandidates.has(el.release_candidate || "Unset")
      );
    };
    const emptyByGroupId = {};
    groupNodes.forEach((g) => (emptyByGroupId[g.id] = false));
    let changed = true;
    while (changed) {
      changed = false;
      groupNodes.forEach((g) => {
        const children = nodes.filter((c) => c.parentNode === g.id);
        const allHidden =
          children.length > 0 &&
          children.every((c) => (c.type === "archimateGroup" ? emptyByGroupId[c.id] : isElementHidden(c)));
        if (emptyByGroupId[g.id] !== allHidden) {
          emptyByGroupId[g.id] = allHidden;
          changed = true;
        }
      });
    }
    return emptyByGroupId;
  }, [nodes, hiddenLayers, hiddenStatuses, hiddenReleaseCandidates]);

  const displayNodes = useMemo(
    () =>
      nodes.map((n) => {
        const el = n.data.element;
        const isGroup = n.type === "archimateGroup";
        const layerHidden = !isGroup && hiddenLayers.has(el.layer);
        const statusHidden = !isGroup && hiddenStatuses.has(el.status || "Dev");
        const rcHidden = !isGroup && hiddenReleaseCandidates.has(el.release_candidate || "Unset");
        const groupEmptyHidden = isGroup && groupEmptyIds[n.id];
        const parentCollapsed = n.parentNode && collapsedGroupIds.has(n.parentNode);
        const hidden = layerHidden || statusHidden || rcHidden || groupEmptyHidden || parentCollapsed;
        const dimmed = neighborInfo && !neighborInfo.ids.has(n.id);
        const groupMin = isGroup ? groupMinSizes[n.id] : null;
        return {
          ...n,
          hidden,
          style: { ...n.style, opacity: dimmed ? 0.15 : 1, transition: "opacity 0.2s ease" },
          data: groupMin ? { ...n.data, ...groupMin } : n.data,
        };
      }),
    [nodes, hiddenLayers, hiddenStatuses, hiddenReleaseCandidates, groupEmptyIds, collapsedGroupIds, neighborInfo, groupMinSizes]
  );

  const displayEdges = useMemo(
    () =>
      edges.map((e) => {
        const dimmed = neighborInfo && !neighborInfo.edgeIds.has(e.id);
        return { ...e, style: { ...e.style, opacity: dimmed ? 0.08 : 1 }, data: { ...e.data, open: e.id === openRelationshipId } };
      }),
    [edges, neighborInfo, openRelationshipId]
  );

  const handleEdgeClick = useCallback((_e, edge) => {
    setOpenRelationshipId((prev) => (prev === edge.id ? null : edge.id));
  }, []);

  const handleNodeDragStop = useCallback(
    (_e, node) => {
      if (node.type === "archimateGroup") {
        onNodeMove(node.id, node.position.x, node.position.y);
        return;
      }
      const abs = getAbsolutePosition(node, nodes);
      const centerX = abs.x + (node.width || 180) / 2;
      const centerY = abs.y + (node.height || 70) / 2;
      const candidates = nodes.filter((n) => n.type === "archimateGroup" && !collapsedGroupIds.has(n.id));
      const matched = candidates.find((g) => {
        const gw = g.style?.width || 260;
        const gh = g.style?.height || 160;
        return centerX >= g.position.x && centerX <= g.position.x + gw && centerY >= g.position.y && centerY <= g.position.y + gh;
      });

      if (matched && node.parentNode !== matched.id) {
        onNodeReparent(node.id, matched.id, abs.x - matched.position.x, abs.y - matched.position.y);
      } else if (!matched && node.parentNode) {
        onNodeReparent(node.id, "", abs.x, abs.y);
      } else {
        onNodeMove(node.id, node.position.x, node.position.y);
      }
    },
    [nodes, collapsedGroupIds, onNodeMove, onNodeReparent]
  );

  const handleConnect = useCallback(
    (params) => {
      const sourceEl = nodes.find((n) => n.id === params.source)?.data?.element;
      const targetEl = nodes.find((n) => n.id === params.target)?.data?.element;
      if (sourceEl && targetEl) onConnectElements(sourceEl.id, targetEl.id);
    },
    [nodes, onConnectElements]
  );

  const handleRfNodeClick = useCallback((_e, node) => {
    setFocusedNodeId((cur) => (cur === node.id ? null : node.id));
  }, []);

  const toggleLayer = useCallback((key) => {
    setHiddenLayers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleStatus = useCallback((status) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const toggleReleaseCandidate = useCallback((rc) => {
    setHiddenReleaseCandidates((prev) => {
      const next = new Set(prev);
      if (next.has(rc)) next.delete(rc);
      else next.add(rc);
      return next;
    });
  }, []);

  const minimapNodeColor = useCallback((node) => {
    const el = node.data?.element;
    if (!el) return "#94A3B8";
    return (LAYER_COLORS[el.layer] || LAYER_COLORS.business).border;
  }, []);

  return (
    <div className="relative h-full w-full bg-slate-50" data-testid="archimate-canvas">
      <EdgeMarkers />
      <div className="absolute top-4 left-3 z-10 flex flex-col gap-2">
        {showKey && (
          <>
            <LayerTogglePanel hiddenLayers={hiddenLayers} onToggle={toggleLayer} />
            <StatusLegendPanel hiddenStatuses={hiddenStatuses} onToggle={toggleStatus} />
            <ReleaseCandidateLegendPanel hiddenReleaseCandidates={hiddenReleaseCandidates} onToggle={toggleReleaseCandidate} />
          </>
        )}
      </div>
      <div
        data-testid="canvas-hint-text"
        className="absolute bottom-4 left-3 z-10 bg-white/90 backdrop-blur border border-slate-200 rounded-md px-2.5 py-1.5 text-[10px] text-slate-500"
      >
        {readOnly ? "Tap an element to view details" : "Shift+click to multi-select \u00b7 Double-click a node for details"}
      </div>
      {focusedNodeId && (
        <button
          data-testid="exit-focus-mode-button"
          onClick={() => setFocusedNodeId(null)}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-blue-600 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-md hover:bg-blue-700 transition-colors"
        >
          <Focus size={12} />
          Exit Focus Mode
        </button>
      )}
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={readOnly ? undefined : handleNodeDragStop}
        onConnect={readOnly ? undefined : handleConnect}
        onNodeClick={handleRfNodeClick}
        onEdgeClick={handleEdgeClick}
        onPaneClick={() => {
          setFocusedNodeId(null);
          setOpenRelationshipId(null);
        }}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        multiSelectionKeyCode="Shift"
        selectionKeyCode="Shift"
        fitView
        minZoom={0.15}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant="dots" gap={20} size={1} color="#CBD5E1" />
        <Controls showInteractive={false} />
        <MiniMap
          data-testid="archimate-minimap"
          pannable
          zoomable
          nodeColor={minimapNodeColor}
          maskColor="rgba(15,23,42,0.08)"
          style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8 }}
        />
      </ReactFlow>
    </div>
  );
}

export function ArchiCanvasWrapper(props) {
  return (
    <ReactFlowProvider>
      <ArchiCanvas {...props} />
    </ReactFlowProvider>
  );
}
